/**
 * Google OAuth 2.0 / OpenID Connect Emulation Plugin
 *
 * Emulates Google's OAuth authorization server for sandboxed apps.
 *
 * Browser-side: Intercepts the redirect to accounts.google.com and redirects
 * the browser back to the app's callback URL with a synthetic auth code.
 *
 * Sandbox-side: Handles the token exchange (POST /token), userinfo endpoint,
 * OIDC discovery, and JWKS endpoint.
 *
 * @module
 */

import crypto from 'node:crypto';
import type {
  EmulationPlugin,
  EmulationRequest,
  PluginContext,
  EmulationResponse,
} from '../plugin.js';
import { generateKeyPair, signIdToken, buildJwks, type EphemeralKeyPair } from './jwt.js';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface GoogleOAuthConfig {
  /** The app's OAuth client ID (used in aud/azp claims). Optional — extracted from requests if not set. */
  clientId?: string;
}

export interface GoogleOAuthUser {
  /** Google user ID (sub claim) */
  sub: string;
  email: string;
  name: string;
  givenName?: string;
  familyName?: string;
  picture?: string;
  emailVerified?: boolean;
  /** Additional claims to include in id_token */
  extraClaims?: Record<string, unknown>;
}

export interface GoogleOAuthFixture {
  user: GoogleOAuthUser;
}

export interface GoogleOAuthState {
  keyPair: EphemeralKeyPair;
  user: GoogleOAuthUser;
  detectedClientId: string | null;
  /** Pre-computed JWKS response (key pair is stable for the container lifetime) */
  jwksResponse: object;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const EMULATED_CODE_PREFIX = 'emulated_code_';
const EMULATED_ACCESS_TOKEN_PREFIX = 'emulated_at_';
const EMULATED_REFRESH_TOKEN_PREFIX = 'emulated_rt_';

export const GOOGLE_OAUTH_DOMAINS = [
  'accounts.google.com',
  'oauth2.googleapis.com',
  'www.googleapis.com',
];

// ─────────────────────────────────────────────────────────────────────────────
// Plugin
// ─────────────────────────────────────────────────────────────────────────────

export const googleOAuthPlugin: EmulationPlugin<
  GoogleOAuthConfig,
  GoogleOAuthFixture,
  GoogleOAuthState
> = {
  id: 'google-oauth',
  name: 'Google OAuth 2.0',
  version: '1.0.0',
  domains: GOOGLE_OAUTH_DOMAINS,
  needsBrowserIntercept: true,

  // ── Sandbox-side routes ──────────────────────────────────────────────────

  routes: [
    // Token exchange
    {
      method: 'POST',
      path: '/token',
      handler: handleTokenExchange,
    },
    {
      method: 'POST',
      path: '/oauth2/v4/token',
      handler: handleTokenExchange,
    },

    // Userinfo
    {
      method: 'GET',
      path: '/oauth2/v3/userinfo',
      handler: handleUserinfo,
    },
    {
      method: 'GET',
      path: '/oauth2/v2/userinfo',
      handler: handleUserinfo,
    },
    {
      method: 'GET',
      path: '/userinfo/v2/me',
      handler: handleUserinfo,
    },

    // OIDC Discovery
    {
      method: 'GET',
      path: '/.well-known/openid-configuration',
      handler: handleOidcDiscovery,
    },

    // JWKS
    {
      method: 'GET',
      path: '/oauth2/v3/certs',
      handler: handleJwks,
    },
  ],

  // ── Browser-side routes ──────────────────────────────────────────────────

  getBrowserRoutes(_fixture) {
    return [
      {
        urlPattern: '**/accounts.google.com/o/oauth2/**',
        handler: async (route, _fix) => {
          const url = new URL(route.request().url());
          const redirectUri = url.searchParams.get('redirect_uri');
          const state = url.searchParams.get('state') || '';

          if (!redirectUri) {
            await route.continue();
            return;
          }

          const code = `${EMULATED_CODE_PREFIX}${crypto.randomUUID()}`;

          // Build callback URL — redirect_uri rewriting (production → sandbox)
          // is handled by the EmulationMiddleware using EnvironmentUrlContext,
          // not here. The middleware rewrites the redirect_uri before this handler
          // sees it, OR we pass the raw redirect_uri and the middleware wraps us.
          //
          // For now, use the redirect_uri as-is — the EmulationMiddleware
          // rewrites it before fulfilling.
          const callbackUrl = new URL(redirectUri);
          callbackUrl.searchParams.set('code', code);
          if (state) callbackUrl.searchParams.set('state', state);

          await route.fulfill({
            status: 302,
            headers: { location: callbackUrl.toString() },
            body: '',
          });
        },
      },
    ];
  },

  // ── Initialization ───────────────────────────────────────────────────────

  initialize(config, fixture) {
    const keyPair = generateKeyPair();
    return {
      keyPair,
      user: fixture.user,
      detectedClientId: config.clientId ?? null,
      jwksResponse: buildJwks(keyPair),
    };
  },

  defaultFixture: {
    user: {
      sub: '100000000000000000001',
      email: 'testuser@example.com',
      name: 'Test User',
      givenName: 'Test',
      familyName: 'User',
      emailVerified: true,
    },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Route Handlers
// ─────────────────────────────────────────────────────────────────────────────

function handleTokenExchange(
  req: EmulationRequest,
  ctx: PluginContext<GoogleOAuthFixture, GoogleOAuthState>
): EmulationResponse | null {
  const params = new URLSearchParams(req.body ?? '');
  const grantType = params.get('grant_type');
  const code = params.get('code');
  const clientId = params.get('client_id') ?? ctx.state.detectedClientId ?? 'unknown';

  // Track client_id for future requests
  if (!ctx.state.detectedClientId && clientId !== 'unknown') {
    ctx.state.detectedClientId = clientId;
  }

  if (grantType === 'authorization_code') {
    if (!code?.startsWith(EMULATED_CODE_PREFIX)) {
      ctx.log('warn', `Token exchange with non-emulated code: ${code}`);
      return { status: 400, body: { error: 'invalid_grant', error_description: 'Invalid authorization code' } };
    }

    const accessToken = `${EMULATED_ACCESS_TOKEN_PREFIX}${crypto.randomUUID()}`;
    const refreshToken = `${EMULATED_REFRESH_TOKEN_PREFIX}${crypto.randomUUID()}`;

    const nonce = params.get('nonce') ?? undefined;
    const idToken = signIdToken(
      {
        iss: 'https://accounts.google.com',
        aud: clientId,
        azp: clientId,
        sub: ctx.state.user.sub,
        email: ctx.state.user.email,
        email_verified: ctx.state.user.emailVerified ?? true,
        name: ctx.state.user.name,
        given_name: ctx.state.user.givenName ?? '',
        family_name: ctx.state.user.familyName ?? '',
        picture: ctx.state.user.picture ?? '',
        nonce,
        ...ctx.state.user.extraClaims,
      },
      ctx.state.keyPair
    );

    ctx.log('info', `Issued tokens for ${ctx.state.user.email}`);

    return {
      status: 200,
      body: {
        access_token: accessToken,
        token_type: 'Bearer',
        expires_in: 3600,
        refresh_token: refreshToken,
        scope: 'openid email profile',
        id_token: idToken,
      },
    };
  }

  if (grantType === 'refresh_token') {
    const accessToken = `${EMULATED_ACCESS_TOKEN_PREFIX}${crypto.randomUUID()}`;

    ctx.log('info', `Refreshed token for ${ctx.state.user.email}`);

    return {
      status: 200,
      body: {
        access_token: accessToken,
        token_type: 'Bearer',
        expires_in: 3600,
        scope: 'openid email profile',
      },
    };
  }

  return { status: 400, body: { error: 'unsupported_grant_type' } };
}

function handleUserinfo(
  _req: EmulationRequest,
  ctx: PluginContext<GoogleOAuthFixture, GoogleOAuthState>
): EmulationResponse {
  const u = ctx.state.user;
  return {
    status: 200,
    body: {
      sub: u.sub,
      email: u.email,
      email_verified: u.emailVerified ?? true,
      name: u.name,
      given_name: u.givenName ?? '',
      family_name: u.familyName ?? '',
      picture: u.picture ?? '',
      ...u.extraClaims,
    },
  };
}

function handleOidcDiscovery(
  _req: EmulationRequest,
  _ctx: PluginContext<GoogleOAuthFixture, GoogleOAuthState>
): EmulationResponse {
  const issuer = 'https://accounts.google.com';
  return {
    status: 200,
    body: {
      issuer,
      authorization_endpoint: `${issuer}/o/oauth2/v2/auth`,
      token_endpoint: 'https://oauth2.googleapis.com/token',
      userinfo_endpoint: 'https://www.googleapis.com/oauth2/v3/userinfo',
      jwks_uri: `${issuer}/oauth2/v3/certs`,
      response_types_supported: ['code', 'token', 'id_token', 'code token', 'code id_token'],
      subject_types_supported: ['public'],
      id_token_signing_alg_values_supported: ['RS256'],
      scopes_supported: ['openid', 'email', 'profile'],
      token_endpoint_auth_methods_supported: ['client_secret_post', 'client_secret_basic'],
      claims_supported: [
        'sub', 'iss', 'aud', 'iat', 'exp', 'name', 'given_name',
        'family_name', 'picture', 'email', 'email_verified', 'nonce',
      ],
    },
  };
}

function handleJwks(
  _req: EmulationRequest,
  ctx: PluginContext<GoogleOAuthFixture, GoogleOAuthState>
): EmulationResponse {
  return {
    status: 200,
    headers: { 'cache-control': 'public, max-age=3600' },
    body: ctx.state.jwksResponse,
  };
}
